import React, { useState, useEffect, useCallback } from 'react';
import { startOfMonth, endOfMonth, differenceInDays } from 'date-fns';
import { useAuth } from '../../contexts/auth/AuthContext';
import { TimeLog, StaffMember, ReportType, Toast } from '../../types/timeReport';
import {
  fetchAllStaff,
  fetchTimeLogs,
  fetchIncompleteShifts,
  fetchActiveShifts,
  getTimeLogById,
} from '../../lib/services/timeReportService';
import {
  generateStaffReports,
  calculateReportSummary,
  validateDateRange,
} from '../../utils/timeReportCalculations';
import {
  generateTimeReportCSV,
  generateStaffReportCSV,
  downloadCSV,
  generateTimeReportPDF,
  generateStaffSummaryPDF,
} from '../../utils/timeReportExport';
import TimeReportFilters from '../../components/admin/time-reports/TimeReportFilters';
import TimeReportSummary from '../../components/admin/time-reports/TimeReportSummary';
import StaffReportTable from '../../components/admin/time-reports/StaffReportTable';
import EditShiftModal from '../../components/admin/time-reports/EditShiftModal';

const ToastNotification: React.FC<{ toast: Toast; onClose: () => void }> = ({ toast, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);

    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor =
    toast.type === 'success'
      ? 'bg-green-500'
      : toast.type === 'error'
      ? 'bg-red-500'
      : 'bg-blue-500';

  return (
    <div
      className={`${bgColor} text-white px-4 py-3 rounded shadow-lg flex justify-between items-center`}
    >
      <span>{toast.message}</span>
      <button onClick={onClose} className="ml-4 text-white focus:outline-none">
        ×
      </button>
    </div>
  );
};

const TimeReports: React.FC = () => {
  const { userProfile } = useAuth();

  // State
  const [startDate, setStartDate] = useState<Date>(startOfMonth(new Date()));
  const [endDate, setEndDate] = useState<Date>(endOfMonth(new Date()));
  const [staffId, setStaffId] = useState<string | null>(null);
  const [reportType, setReportType] = useState<ReportType>('weekly');

  const [staffMembers, setStaffMembers] = useState<StaffMember[]>([]);
  const [timeLogs, setTimeLogs] = useState<TimeLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [editingShift, setEditingShift] = useState<TimeLog | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const [toasts, setToasts] = useState<Toast[]>([]);

  // Toast management
  const addToast = useCallback((message: string, type: 'success' | 'error' | 'info' = 'info') => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
  }, []);

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  // Load staff members
  const loadStaffMembers = useCallback(async () => {
    try {
      const staff = await fetchAllStaff();
      setStaffMembers(staff);
    } catch (err: any) {
      addToast(`Failed to load staff: ${err.message}`, 'error');
    }
  }, [addToast]);

  // Load time logs when filters change
  const loadTimeLogs = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Validate date range
      if (!validateDateRange(startDate, endDate)) {
        setError('Date range cannot exceed 6 months');
        setIsLoading(false);
        return;
      }

      if (endDate < startDate) {
        setError('End date must be after start date');
        setIsLoading(false);
        return;
      }

      const logs = await fetchTimeLogs(startDate, endDate, staffId);
      setTimeLogs(logs);
    } catch (err: any) {
      setError(err.message || 'Failed to load time logs');
      addToast(`Failed to load time logs: ${err.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate, staffId, addToast]);

  useEffect(() => {
    loadStaffMembers();
  }, [loadStaffMembers]);

  useEffect(() => {
    loadTimeLogs();
  }, [loadTimeLogs]);

  const handleStartDateChange = (date: Date) => {
    setStartDate(date);

    // Validate date range
    if (differenceInDays(endDate, date) > 180) {
      const maxEndDate = new Date(date);
      maxEndDate.setDate(maxEndDate.getDate() + 180);
      setEndDate(maxEndDate);
      addToast('End date adjusted to 6-month limit', 'info');
    }
  };

  const handleEndDateChange = (date: Date) => {
    setEndDate(date);

    // Validate date range
    if (differenceInDays(date, startDate) > 180) {
      const maxStartDate = new Date(date);
      maxStartDate.setDate(maxStartDate.getDate() - 180);
      setStartDate(maxStartDate);
      addToast('Start date adjusted to 6-month limit', 'info');
    }
  };

  const handleEditShift = async (shiftId: string) => {
    try {
      const shift = await getTimeLogById(shiftId);
      if (shift) {
        setEditingShift(shift);
        setShowEditModal(true);
      } else {
        addToast('Shift not found', 'error');
      }
    } catch (err: any) {
      addToast(`Failed to load shift: ${err.message}`, 'error');
    }
  };

  const handleSaveShift = () => {
    addToast('Shift updated successfully', 'success');
    loadTimeLogs(); // Refresh data
  };

  const handleExportCSV = () => {
    try {
      const staffReports = generateStaffReports(timeLogs);

      let csvContent: string;
      let filename: string;

      if (staffId) {
        // Single staff member - detailed log
        const staffName = staffMembers.find((s) => s.id === staffId)?.name || 'Staff';
        csvContent = generateTimeReportCSV(timeLogs, staffName);
        filename = `TimeReport_${staffName.replace(/\s+/g, '_')}_${startDate.toISOString().split('T')[0]}.csv`;
      } else {
        // All staff - summary report
        csvContent = generateStaffReportCSV(staffReports);
        filename = `StaffSummary_${startDate.toISOString().split('T')[0]}.csv`;
      }

      downloadCSV(csvContent, filename);
      addToast('Report exported to CSV', 'success');
    } catch (err: any) {
      addToast(`Failed to export CSV: ${err.message}`, 'error');
    }
  };

  const handleExportPDF = async () => {
    try {
      const staffReports = generateStaffReports(timeLogs);

      if (staffId) {
        // Single staff member - detailed report
        const staffName = staffMembers.find((s) => s.id === staffId)?.name || 'Staff';
        await generateTimeReportPDF(timeLogs, staffName, startDate, endDate);
      } else {
        // All staff - summary report
        await generateStaffSummaryPDF(staffReports, startDate, endDate);
      }

      addToast('Report exported to PDF', 'success');
    } catch (err: any) {
      addToast(`Failed to export PDF: ${err.message}`, 'error');
    }
  };

  // Calculate summary and reports
  const summary = calculateReportSummary(timeLogs);
  const staffReports = generateStaffReports(timeLogs);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Time Reports</h1>
          <p className="text-sm text-gray-600 mt-1">
            View and manage crew time logs and generate reports
          </p>
        </div>
      </div>

      {/* Filters */}
      <TimeReportFilters
        startDate={startDate}
        endDate={endDate}
        staffId={staffId}
        reportType={reportType}
        staffMembers={staffMembers}
        onStartDateChange={handleStartDateChange}
        onEndDateChange={handleEndDateChange}
        onStaffChange={setStaffId}
        onReportTypeChange={setReportType}
        onExportCSV={handleExportCSV}
        onExportPDF={handleExportPDF}
      />

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="bg-white rounded-lg shadow p-12">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            <span className="ml-4 text-gray-600">Loading time reports...</span>
          </div>
        </div>
      )}

      {/* Data Display */}
      {!isLoading && !error && (
        <>
          {/* Summary Cards */}
          <TimeReportSummary summary={summary} />

          {/* Staff Report Table */}
          <StaffReportTable
            reports={staffReports}
            reportType={reportType}
            startDate={startDate}
            endDate={endDate}
            onEditShift={handleEditShift}
          />
        </>
      )}

      {/* Edit Shift Modal */}
      <EditShiftModal
        shift={editingShift}
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingShift(null);
        }}
        onSave={handleSaveShift}
        adminUserId={userProfile?.id || ''}
      />

      {/* Toast Notifications */}
      <div className="fixed bottom-4 right-4 z-50 space-y-2">
        {toasts.map((toast) => (
          <ToastNotification key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
        ))}
      </div>
    </div>
  );
};

export default TimeReports;
